import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, ButtonGroup, Col, Dropdown, FormLabel, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import FilterAccordion from "../../../Common/Filter/FilterAccordion";
import { errorAlert, warnAlert } from "../../../alert/defaultAlert";
import { SearchOperation } from "../../../../../utils/ApiUtils";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import GridCustomBase from "../../../GridCustom/GridCustomBase";
import Common from "../../../../../constants/Common";
import { buildWhatsappUrl } from "../../../../../utils/FormatUtils";
import InputText from "../../../Forms/Element/InputText";
import { useNavigate } from "react-router-dom";
import MenuConfig from "../../../../../constants/MenuConfig";
import { useBranch } from "../../../../../context/BranchContext";
import EditIcon from "../../../Icon/EditIcon";
import GridCustomCheckBoxRow from "../../../GridCustom/GridCustomCheckBoxRow";
import BranchSelect from "../../../Custom/Main/Branch/BranchSelect";
import VendorSelect from "../../../Custom/Main/Vendor/VendorSelect";
import StockStatusSelect from "./StockStatusSelect";
import { formatQuantityByUnit, getUnitLabel } from "../../../../../utils/unitMeasure";
import "./StockHistory.css";

const StockHistory = () => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const [product, setProduct] = useState('');
    const [lowStock, setLowStock] = useState(null); // Estoque baixo
    const [statusFilter, setStatusFilter] = useState(null); // Novo: filtro por status
    const [branchFilter, setBranchFilter] = useState(null);
    const [vendorFilter, setVendorFilter] = useState(null);
    const [showLoading, setShowLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [stats, setStats] = useState({ outOfStock: 0, lowStock: 0, normalStock: 0, total: 0 });
    const navigate = useNavigate();

    // Grid
    const [pageable, setPageable] = useState(null);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    const [size, setSize] = useState(Common.DEFAULT_ROWS_PER_PAGE);

    const buildFilters = useCallback((options = {}) => {
        const { includeManualBranch = false, statusValue = statusFilter } = options;
        let filters = [];

        // REGRA: Se há contexto (selectedBranch), usa ele. Senão, NÃO usa filtro de filial automaticamente.
        // O filtro manual (branchFilter) só é usado quando o botão Pesquisar é clicado.
        if (selectedBranch !== null) {
            filters.push({
                field: "branch.id",
                value: selectedBranch.id,
                operation: SearchOperation.EQUAL
            });
        }
        else if (includeManualBranch && branchFilter !== null) {
            filters.push({
                field: "branch.id",
                value: branchFilter.value,
                operation: SearchOperation.EQUAL
            });
        }

        if (product) {
            filters.push({
                field: "product.name",
                value: product,
                operation: SearchOperation.MATCH
            });
        }

        if (vendorFilter) {
            filters.push({
                field: "product.vendor.id",
                value: vendorFilter.value,
                operation: SearchOperation.EQUAL
            });
        }

        // Filtro por status (substitui o filtro lowStock quando statusFilter está ativo)
        if (statusValue) {
            if (statusValue.value === 'out_of_stock') {
                filters.push({
                    field: "qty",
                    operation: SearchOperation.EQUAL,
                    value: 0
                });
            } else if (statusValue.value === 'low_stock') {
                // Estoque baixo: qty > 0 E lowStock = true
                filters.push({
                    field: "lowStock",
                    operation: SearchOperation.EQUAL,
                    value: true
                });
            } else if (statusValue.value === 'normal_stock') {
                // Estoque normal: qty > 0 E lowStock = false
                filters.push({
                    field: "lowStock",
                    operation: SearchOperation.EQUAL,
                    value: false
                });
                // Garantir que não traz produtos com estoque zerado
                filters.push({
                    field: "qty",
                    operation: SearchOperation.GREATER_THAN,
                    value: 0
                });
            }
        } else if (lowStock) {
            // Mantém compatibilidade com o filtro antigo
            if (lowStock.value === 'N') {
                filters.push({
                    field: "lowStock",
                    operation: SearchOperation.EQUAL,
                    value: false
                });
            } else {
                filters.push({
                    field: "lowStock",
                    operation: SearchOperation.EQUAL,
                    value: true
                });
            }
        }
        return filters;
    }, [branchFilter, lowStock, product, selectedBranch, statusFilter, vendorFilter]);

    function search() {
        executeSearch(buildFilters());
    }

    // Função chamada pelo botão Pesquisar - usa branchFilter se contexto for null
    function handleSearchClick() {
        executeSearch(buildFilters({ includeManualBranch: true }));
    }

    // Função que executa a busca com os filtros fornecidos
    const executeSearch = useCallback((filters) => {
        let payload = {
            filters: filters,
            page: page,
            size: size,
            orders: [{
                field: "qty",
                direction: "ASC"
            }]
        }

        setShowLoading(true);
        
        // Busca as estatísticas globais (todos os registros)
        const statsPayload = {
            filters: filters,
            page: 0,
            size: 999999, // Busca todos para calcular estatísticas
            orders: [{
                field: "qty",
                direction: "ASC"
            }]
        };
        
        axiosInstance.post(Api.stock_control.stock + "/pageable", statsPayload)
            .then((statsResponse) => {
                const allContent = statsResponse.data.content;
                // Categorias mutuamente exclusivas baseadas no campo lowStock do backend
                const outOfStock = allContent.filter(item => item.qty === 0).length;
                const lowStock = allContent.filter(item => item.lowStock === true).length;
                const normalStock = allContent.filter(item => item.qty > 0 && item.lowStock === false).length;
                setStats({ outOfStock, lowStock, normalStock, total: allContent.length });
            })
            .catch(error => console.error('Error loading stats:', error));

        // Busca os dados paginados para a tabela
        axiosInstance.post(Api.stock_control.stock + "/pageable", payload)
            .then((response) => {
                setPageable(response.data);
                setRows(response.data.content);
            })
            .catch(error => errorAlert(error))
            .finally(() => {
                setShowLoading(false);
            });
    }, [page, size]);

    const redirectToNew = useCallback(() => {
        if (selectedRows.length === 0) {
            warnAlert(t('validation.required.row'));
            return;
        }
        if (selectedRows.length > 1) {
            warnAlert(t('validation.required.row_select_one'));
            return;
        }

        navigate(MenuConfig.stock_control.stock.route + "/new/" + selectedRows[0]);
    }, [selectedRows, t, navigate]);

    // Função para exportar relatório
    const exportReport = useCallback(() => {
        // Cria os dados para exportação
        const exportData = rows.map((row, index) => {
            const unitLabel = getUnitLabel(row.product.unit, t);
            const status = row.qty === 0 ? t('out_of_stock') : 
                          (row.qty > 0 && row.qty < row.minStockLevel ? t('low_stock') : t('normal_stock'));
            
            return {
                '#': index + 1,
                [t('product')]: row.product.name,
                [t('branch')]: row.branch.name,
                [t('vendor')]: row.product.vendor ? row.product.vendor.name : t('no_vendor'),
                [t('phone')]: row.product.vendor && row.product.vendor.phone ? row.product.vendor.phone : '-',
                [t('status')]: status,
                [t('current_stock')]: formatQuantityByUnit(row.qty, row.product.unit) + ' ' + unitLabel,
                [t('min_stock_level')]: formatQuantityByUnit(row.minStockLevel, row.product.unit) + ' ' + unitLabel
            };
        });

        // Converte para CSV
        if (exportData.length === 0) {
            warnAlert(t('no_data_to_export'));
            return;
        }

        const headers = Object.keys(exportData[0]);
        const csvContent = [
            headers.join(';'),
            ...exportData.map(row => headers.map(header => row[header]).join(';'))
        ].join('\n');

        // Cria o arquivo e faz download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `relatorio_estoque_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [rows, t]);

    const applyQuickStatusFilter = useCallback((status) => {
        const nextStatus = status === 'all' || statusFilter?.value === status
            ? null
            : {
                value: status,
                label: t(status)
            };

        setStatusFilter(nextStatus);
        setLowStock(null);
        setPage(0);
        executeSearch(buildFilters({ includeManualBranch: true, statusValue: nextStatus }));
    }, [buildFilters, executeSearch, statusFilter, t]);

    // Função para limpar todos os filtros
    const handleClearFilters = useCallback(() => {
        setProduct('');
        setLowStock(null);
        setStatusFilter(null);
        setVendorFilter(null);
        setPage(0);
        // Preserva a filial do contexto, se houver
        if (selectedBranch !== null) {
            setBranchFilter({
                value: selectedBranch.id,
                label: selectedBranch.name
            });
        } else {
            setBranchFilter(null);
        }

        // Os estados são assíncronos; monta o filtro-base diretamente para não
        // repetir a consulta com valores antigos ao clicar em Limpar.
        const baseFilters = selectedBranch !== null ? [{
            field: "branch.id",
            value: selectedBranch.id,
            operation: SearchOperation.EQUAL
        }] : [];
        executeSearch(baseFilters);
    }, [executeSearch, selectedBranch]);

    // Sincroniza o filtro de filial com o contexto quando ele muda
    useEffect(() => {
        if (selectedBranch !== null) {
            setBranchFilter({
                value: selectedBranch.id,
                label: selectedBranch.name
            });
        } else {
            setBranchFilter(null);
        }
    }, [selectedBranch]);

    // Busca quando mudar paginação ou contexto de filial
    useEffect(() => {
        search();
        // eslint-disable-next-line
    }, [page, size, selectedBranch]);

    const quickFilters = [
        {
            key: 'out_of_stock',
            value: stats.outOfStock,
            icon: 'fa-solid fa-siren-on',
            accentClass: 'stock-history__metric-card--danger',
            valueClass: 'text-danger'
        },
        {
            key: 'low_stock',
            value: stats.lowStock,
            icon: 'fas fa-exclamation-triangle',
            accentClass: 'stock-history__metric-card--warning',
            valueClass: 'text-warning'
        },
        {
            key: 'normal_stock',
            value: stats.normalStock,
            icon: 'fas fa-check-circle',
            accentClass: 'stock-history__metric-card--success',
            valueClass: 'text-success'
        },
        {
            key: 'total',
            value: stats.total,
            icon: 'fas fa-boxes',
            accentClass: 'stock-history__metric-card--primary',
            valueClass: 'text-primary'
        }
    ];

    const getRowStatusMeta = (row) => {
        if (row.qty === 0) {
            return {
                label: t('out_of_stock'),
                badgeClass: 'danger',
                iconClass: 'fa-solid fa-siren-on text-danger fa-beat',
                quantityClass: 'text-danger',
                rowClass: 'stock-history__table-row--danger'
            };
        }

        if (row.qty > 0 && row.qty < row.minStockLevel) {
            return {
                label: t('low_stock'),
                badgeClass: 'warning',
                iconClass: 'fas fa-exclamation-triangle text-warning',
                quantityClass: 'text-warning',
                rowClass: 'stock-history__table-row--warning'
            };
        }

        return {
            label: t('normal_stock'),
            badgeClass: 'success',
            iconClass: 'fas fa-check-circle text-success',
            quantityClass: 'text-success',
            rowClass: 'stock-history__table-row--success'
        };
    };

    return (
        <>
            <div className="stock-history__filter-shell mb-4">
                <FilterAccordion
                    title={t('filters')}
                    onSearch={handleSearchClick}
                    onClear={handleClearFilters}
                >
                    <Row className="mb-3">
                        <Col md={12}>
                            <small className="text-muted">{t('stock_filters_hint')}</small>
                        </Col>
                    </Row>
                    <Row className="g-3">
                        <Col lg={4} md={6}>
                            <FormLabel>{t('product')}</FormLabel>
                            <InputText
                                value={product}
                                onChange={(e) => {
                                    setProduct(e.target.value);
                                }}
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <FormLabel>{t('branch')}</FormLabel>
                            <BranchSelect
                                value={branchFilter}
                                onChange={(value) => {
                                    setBranchFilter(value);
                                }}
                                isDisabled={selectedBranch !== null}
                                isClearable={true}
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <FormLabel>{t('vendor')}</FormLabel>
                            <VendorSelect
                                value={vendorFilter}
                                onChange={(value) => {
                                    setVendorFilter(value);
                                }}
                            />
                        </Col>
                        <Col lg={2} md={6}>
                            <FormLabel>{t('status')}</FormLabel>
                            <StockStatusSelect
                                value={statusFilter}
                                onChange={(value) => {
                                    setStatusFilter(value);
                                    setLowStock(null);
                                }}
                                isClearable={true}
                            />
                        </Col>
                    </Row>
                </FilterAccordion>
            </div>

            <div className="stock-history__metrics mb-4">
                {quickFilters.map((filter) => {
                    const isActive = filter.key === 'total'
                        ? statusFilter === null
                        : statusFilter?.value === filter.key;
                    const actionFilter = filter.key === 'total' ? 'all' : filter.key;

                    return (
                        <button
                            key={filter.key}
                            type="button"
                            className={[
                                'stock-history__metric-card',
                                filter.accentClass,
                                isActive ? 'stock-history__metric-card--active' : ''
                            ].join(' ').trim()}
                            onClick={() => applyQuickStatusFilter(actionFilter)}
                        >
                            <div className="stock-history__metric-head">
                                <div>
                                    <span className="stock-history__metric-label">{t(filter.key)}</span>
                                    <strong className={['stock-history__metric-value', filter.valueClass].join(' ')}>{filter.value}</strong>
                                </div>
                                <span className="stock-history__metric-icon">
                                    <i className={filter.icon}></i>
                                </span>
                            </div>
                            <span className="stock-history__metric-caption">{isActive ? t('selected') : t('filters')}</span>
                        </button>
                    );
                })}
            </div>

            <Row className="mb-3">
                <Col md={12}>
                    <ButtonGroup>
                        <Dropdown>
                            <Dropdown.Toggle className="me-2" variant="primary">
                                {t('actions')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={redirectToNew}>
                                    <EditIcon /> {t('update')}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={exportReport} disabled={rows.length === 0}>
                                    <i className="fas fa-file-excel me-2"></i>{t('export_report')}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonGroup>
                </Col>
            </Row>

            <GridCustomBase
                pageable={pageable}
                setPage={setPage}
                setSize={setSize}
                showLoading={showLoading}
                selectedCount={selectedRows.length}
                selectionMode="single">

                <div className="table-responsive">
                <table className="table mb-0 stock-history__table">
                    <thead>
                        <tr>
                            <th style={{ width: 50 }} aria-label={t('select')}></th>
                            <th style={{ width: 250 }}>{t("product")}</th>
                            <th style={{ width: 150 }}>{t("branch")}</th>
                            <th style={{ width: 200 }}>{t("vendor")}</th>
                            <th className="text-center" style={{ width: 120 }}>{t("status")}</th>
                            <th className="text-center stock-history__quantity-column" style={{ width: 150 }}>{t("current_stock")}</th>
                            <th className="text-center stock-history__quantity-column" style={{ width: 150 }}>{t("min_stock_level")}</th>
                            <th className="bg-none"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan="8" className="text-center py-5">
                                    <div className="text-muted">
                                        <i className="fas fa-box-open fa-2x mb-3 d-block opacity-50"></i>
                                        <strong className="d-block mb-1">{t('no_stock_results')}</strong>
                                        <span>{t('stock_empty_hint')}</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {rows.map((row, index) => {
                            const unitLabel = getUnitLabel(row.product.unit, t);
                            const statusMeta = getRowStatusMeta(row);

                            return (
                                <tr key={index} className={["align-middle h6", "stock-history__table-row", statusMeta.rowClass].join(' ')}>
                                    <GridCustomCheckBoxRow row={row} setSelectedRows={setSelectedRows} singleSelect={true} />
                                    <td className="stock-history__product-cell">
                                        <div className="stock-history__product-main">
                                            <span className="stock-history__product-icon">
                                                <i className={statusMeta.iconClass}></i>
                                            </span>
                                            <div>
                                                <div className="stock-history__product-name">{row.product.name}</div>
                                                <small className="text-muted">{unitLabel}</small>
                                            </div>
                                        </div>
                                    </td>
                                    <td>{row.branch.name}</td>
                                    <td>
                                        {row.product.vendor ? (
                                            <div className="d-flex align-items-center justify-content-between gap-2">
                                                <div className="d-flex flex-column">
                                                    <span className="text-truncate fw-bold" style={{ maxWidth: '160px' }}>
                                                        {row.product.vendor.name}
                                                    </span>
                                                    {row.product.vendor.phone && (
                                                        <small className="text-muted">
                                                            <i className="fas fa-phone me-1"></i>
                                                            {row.product.vendor.phone}
                                                        </small>
                                                    )}
                                                </div>
                                                {row.product.vendor.phone && (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip>{t('contact_whatsapp')}</Tooltip>}
                                                    >
                                                        <a
                                                            href={buildWhatsappUrl(row.product.vendor.phone)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-success btn-xs ms-2 stock-history__whatsapp-button"
                                                        >
                                                            <i className="fab fa-whatsapp"></i>
                                                        </a>
                                                    </OverlayTrigger>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-muted fst-italic">{t('no_vendor')}</span>
                                        )}
                                    </td>
                                    <td className="text-center stock-history__quantity-column">
                                        <Badge bg={statusMeta.badgeClass} text={statusMeta.badgeClass === 'warning' ? 'dark' : undefined} className="px-3 py-2 stock-history__status-badge">
                                            <i className={[statusMeta.iconClass.split(' ').find((item) => item.startsWith('fa')) || 'fas fa-circle', 'me-1'].join(' ')}></i>
                                            {statusMeta.label}
                                        </Badge>
                                    </td>
                                    <td className="text-center stock-history__quantity-column">
                                        <div className={statusMeta.quantityClass}>
                                            <strong className="fs-5">{formatQuantityByUnit(row.qty, row.product.unit)}</strong>
                                            <small className="d-block text-muted">{unitLabel}</small>
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        <div>
                                            <strong className="fs-5">{formatQuantityByUnit(row.minStockLevel, row.product.unit)}</strong>
                                            <small className="d-block text-muted">{unitLabel}</small>
                                        </div>
                                    </td>
                                    <td></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
            </GridCustomBase>
        </>
    );
}

export default StockHistory;
